import React from 'react';
import axios from 'axios';
import { withTheme } from 'styled-components';

import { StyledCookieConsent, StyledCookieText, StyledButton, StyledLink, StyledWrapper } from './CookieConsent.styled';

class CookieConsent extends React.Component {
  constructor(props) {
    super(props);

    const { languageCode, environment } = this.props;

    this.onPrivacyStatementClick = this.onPrivacyStatementClick.bind(this);
    this.onAcceptClicked = this.onAcceptClicked.bind(this);

    this.state = {
      previouslyAccepted: false,
      accepted: false,
      languageCode: languageCode || 'en',
      environment: environment || 'test' // eslint-disable-line react/no-unused-state
    };

    this.cookieConsentWrapper = React.createRef();
  }

  async componentDidMount() {
    await import('../../utils/window.truenth.ga');
    await import('../../utils/window.truenth.hotjar');
    // TODO: Improve cookie check
    if (
      window.truenth.cookies.getCookie('cookieConsent') ||
      // Check for cookie in localStorage when loading MLM from OA
      localStorage.getItem('cookiesAcceptedAt')
    ) {
      this.setState(
        {
          previouslyAccepted: true
        },
        () => {
          this.initGA();
        }
      );
      return;
    }
    this.setAnimationFinishGAEvent();
  }

  /**
   * Privacy statement link cleck event handler
   * @param {Object} e The event object
   */
  onPrivacyStatementClick(e) {
    e.preventDefault();
    const { innerHTML, href } = e.target;
    this.postCustomGAHit('navigation', 'link', `Accept Cookies Prompt | ${innerHTML}`).then(() => {
      window.location = href;
    });
  }

  /**
   * Accept button event handler. Fired when the user click Accept in the cookie consent
   * @param {Object} e The event object
   */
  onAcceptClicked(e) {
    e.preventDefault();
    this.setState({ accepted: true });
    this.setCookie();
    if (window.truenth.utils.hasConsented() === true) {
      // loading ga
      this.initGA();

      // loading hotjar
      window.truenth.hotjar.init();

      // set the language cookie
      window.truenth.cookies.setCookie('django_language', 'en');
    }
    const linkText = e.target.innerHTML;
    this.postCustomGAHit('navigation', 'link', `Accept Cookies Prompt | ${linkText}`);
  }

  /**
   * Sets the 'cookieConsent' cookie with the current time in UTC as value
   */
  setCookie = () => {
    const utcTimestamp = new Date().getTime();

    const TEN_YEARS_OFFSET_SECS = 10 * 365 * 24 * 60 * 60;
    window.truenth.cookies.setCookie('cookieConsent', utcTimestamp, {
      expiry: new Date(utcTimestamp + TEN_YEARS_OFFSET_SECS * 1000)
    });

    // Set cookie for OA in localStorage
    const newCookiesAcceptedAt = new Date().toString();
    localStorage.setItem('cookiesAcceptedAt', newCookiesAcceptedAt);
  };

  /**
   * Gets total duration of the CSS animation of the cookie consent banner
   * @return {number} the total duration of the animation (in seconds)
   */
  getAnimationDuration() {
    const animationDuration = parseFloat(
      getComputedStyle(this.cookieConsentWrapper.current)['animation-duration'].replace('s', '')
    );
    const animationDelay = parseFloat(
      getComputedStyle(this.cookieConsentWrapper.current)['animation-delay'].replace('s', '')
    );
    return animationDuration + animationDelay;
  }

  /**
   * Sets a timer to hit GA when cookie consent banner is completely visible
   */
  setAnimationFinishGAEvent() {
    const animationMilliseconds = this.getAnimationDuration() * 1000;
    setTimeout(() => {
      this.postCustomGAHit('notification', 'alert', 'Accept Cookies Prompt | view');
    }, animationMilliseconds);
  }

  /**
   * Makes a POST request to GA endpoint with the corresponding parameters
   * @param {string} eventCreate GA event create
   * @param {string} eventAlert GA event alert
   * @param {string} eventLeave GA event leave
   * @returns {Promise} the Axios post request promise
   */
  postCustomGAHit = (eventCreate, eventAlert, eventLeave) => {
    const uid = 'consent.1';

    const pid = process.env.REACT_APP_GA_ID;

    const encodedEventCreate = encodeURIComponent(eventCreate);
    const encodedEventAlert = encodeURIComponent(eventAlert);
    const encodedEventLeave = encodeURIComponent(eventLeave);
    const host = encodeURIComponent(document.location.host);
    const pageTitle = encodeURIComponent(document.title);
    const path = encodeURIComponent(document.location.pathname);
    const hit = `v=1&t=event&tid=${pid}&cid=${uid}&ds=client-measurement-protocol&ni=1&dh=${host}&dp=${path}&dt=${pageTitle}&ec=${encodedEventCreate}&ea=${encodedEventAlert}&el=${encodedEventLeave}`;

    return axios
      .post(`https://www.google-analytics.com/collect?${hit}`, null, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw Error(error);
        // Do nothing
      });
  };

  initGA() {
    const { theme, gaSnippet } = this.props;

    if (window.truenth.ga.canHaveGA()) {
      // loading ga
      window.truenth.ga.init(theme.breakpoints);
      gaSnippet();
      window.truenth.ga.enableTracking();
    }
  }

  render() {
    const { previouslyAccepted, accepted, languageCode } = this.state;

    return (
      <>
        {!previouslyAccepted && (
          <StyledCookieConsent accepted={accepted} ref={this.cookieConsentWrapper}>
            <StyledWrapper accepted={accepted}>
              <StyledCookieText tag="p" level="small">
                Our website uses cookies to bring you an easy and personalized browsing experience. By continuing, you
                consent to our cookie policy on this device. For more info, please see our&nbsp;
                <StyledLink href={`/${languageCode}/privacy-statement`} onClick={this.onPrivacyStatementClick}>
                  Privacy Statement
                </StyledLink>
                .
              </StyledCookieText>
              <StyledButton onClick={this.onAcceptClicked}>Accept Cookies</StyledButton>
            </StyledWrapper>
          </StyledCookieConsent>
        )}
      </>
    );
  }
}

export default withTheme(CookieConsent);
